/**
 * AiM Solo 2 Wi-Fi download protocol (TCP 2000, one UDP probe to 36002).
 *
 * Written from a packet capture of AiM's own iOS app talking to this project's Solo 2
 * (2026-10-16, firmware with the WF121 Wi-Fi module), which matches the clean-room notes of
 * Zenardi/racestudio-macos (MIT, MyChron6 captures). Framing and CSV parsing were first ported
 * from delta-bridge (MIT). The socket primitives are behind {@link AimBridge}, so the same code
 * runs on the native iOS plugin today and could run on a desktop helper later.
 *
 * Wire summary (all integers little-endian):
 * - One UDP probe `aim-ka` to 10.0.0.1:36002; the logger answers with a 236-byte identity
 *   block (device name at 0x14). No keepalive follows.
 * - Frames on TCP 2000: `<hSTCP`, u32 payload length, 0x00, `>`, payload, `<STCP`,
 *   u16 checksum (sum of payload bytes mod 65536), `>`.
 * - Hello: client payload `00000000 06 08 0000`, logger answers `… 06 09 …`.
 * - Every command is one transaction on a 64-byte payload: opcode u16 @8, subcode u16 @10,
 *   upload length u32 @16, tag u32 0x0a01 @24, NUL-padded path from @32.
 *     client  command
 *     logger  echo, tag 0x0a01 "send the upload"   (only when @16 > 0)
 *     client  [u32 0][upload bytes]
 *     logger  4-byte ack
 *     logger  echo, tag 0x0a09 "accepted"          (when there is no upload)
 *     logger  echo, tag 0x0a11: response length @16, chunk stride @20 (0x7fc0 on the Solo 2)
 *     client  ack(0); logger chunk [u32 offset][data]; client ack(next offset) … until done
 * - First command after the hello is session-open 0x10/1 with a 64-byte clock upload and the
 *   u32 2 at @32; then info reads 0x02/2, 0x08/2, 0x03/2; the session list is 0x24/2 (a CSV
 *   `name,size,date,hour,nlap,…`); a file is read with 0x02/4 and path `1:/mem/<name>`,
 *   arriving zlib-compressed (.xrz), which the .xrk decoder inflates.
 */

export interface AimBridge {
  /** Send the UDP probe and resolve with the logger's reply (empty if it stayed silent). */
  discover(signal?: AbortSignal): Promise<Uint8Array>
  stopDiscovery(): Promise<void>
  tcpConnect(signal?: AbortSignal): Promise<void>
  tcpClose(): Promise<void>
  tcpWrite(data: Uint8Array, signal?: AbortSignal): Promise<void>
  /** Bytes received within `timeoutMs` (possibly empty), at most `capacity`. */
  tcpRead(timeoutMs: number, capacity?: number, signal?: AbortSignal): Promise<Uint8Array>
  /** Human-readable notes from the transport (interface, ports) for the protocol log. */
  notes?: string[]
}

export class AimError extends Error {
  readonly code: string
  constructor(message: string, code = 'AIM_ERROR') {
    super(message)
    this.name = 'AimError'
    this.code = code
  }
}

export const AIM_HOST = '10.0.0.1'
export const AIM_TCP_PORT = 2000
export const AIM_UDP_PORT = 36002
export const AIM_KEEPALIVE = 'aim-ka'
/** The AiM app sends the probe once; 0 means no repeat. */
export const AIM_KEEPALIVE_INTERVAL_MS = 0

const AIM_MAX_FRAME_PAYLOAD = 8 * 1024 * 1024
const AIM_MAX_RESPONSE = 64 * 1024 * 1024
const HELLO_PAYLOAD = new Uint8Array([0, 0, 0, 0, 6, 8, 0, 0])
const RESPONSE_TIMEOUT_MS = 8_000
/** Mid-transfer, a chunk may take a while (the logger reads flash, Wi-Fi hiccups): the ack is repeated this many times before giving up. */
const CHUNK_RETRIES = 5

export const TAG_REQUEST = 0x0a01
export const TAG_CLOSE = 0x0a00
export const TAG_ACCEPTED = 0x0a09
export const TAG_HEADER = 0x0a11
/** Seen on the Solo 2 while it is still building the catalog: "not ready", length 0. */
export const TAG_BUSY = 0x0a1d

export type FrameType = 'STCP' | 'STNC'

export interface AimFrame {
  type: FrameType
  payload: Uint8Array
}

export interface AimSession {
  /** File name on the logger, e.g. `a_0394.xrz` */
  name: string
  /** True for .xrz/.xrk, and .hrz, which the logger lists like sessions and the decoder is let to try */
  supported: boolean
  /** On-device path, `1:/mem/<name>` */
  path: string
  /** Stored (compressed) size in bytes */
  size: number
  /** `dd/mm/yyyy` as the logger reports it */
  date: string
  /** `hh:mm:ss` device-local, when present */
  time?: string
  laps?: number
  /** Best lap in milliseconds, when present */
  bestMs?: number
  track?: string
}

export interface AimDevice {
  /** Device name from the discovery reply, e.g. "Solo 2 ID Tina" */
  name: string
}

// ---------- bytes ----------

const ascii = new TextEncoder()
const utf8 = new TextDecoder()

function u32(buf: Uint8Array, at: number): number {
  return new DataView(buf.buffer, buf.byteOffset, buf.byteLength).getUint32(at, true)
}
function putU32(buf: Uint8Array, at: number, v: number) {
  new DataView(buf.buffer, buf.byteOffset, buf.byteLength).setUint32(at, v >>> 0, true)
}
function putU16(buf: Uint8Array, at: number, v: number) {
  new DataView(buf.buffer, buf.byteOffset, buf.byteLength).setUint16(at, v & 0xffff, true)
}
function concat(parts: Uint8Array[], total?: number): Uint8Array {
  const n = total ?? parts.reduce((a, p) => a + p.length, 0)
  const out = new Uint8Array(n)
  let o = 0
  for (const p of parts) {
    out.set(p.subarray(0, Math.min(p.length, n - o)), o)
    o += p.length
    if (o >= n) break
  }
  return out
}
function hex(buf: Uint8Array, n = 20): string {
  let s = ''
  for (let i = 0; i < Math.min(n, buf.length); i++) s += buf[i].toString(16).padStart(2, '0')
  return s + (buf.length > n ? '…' : '')
}
function indexOf(buf: Uint8Array, needle: Uint8Array, from = 0): number {
  outer: for (let i = from; i <= buf.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) if (buf[i + j] !== needle[j]) continue outer
    return i
  }
  return -1
}

// ---------- framing ----------

export function aimChecksum(payload: Uint8Array): number {
  let sum = 0
  for (const b of payload) sum = (sum + b) & 0xffff
  return sum
}

export function encodeAimFrame(type: FrameType, payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + payload.length + 8)
  out.set(ascii.encode(`<h${type}`), 0)
  putU32(out, 6, payload.length)
  out[10] = 0
  out[11] = 0x3e
  out.set(payload, 12)
  const f = 12 + payload.length
  out.set(ascii.encode(`<${type}`), f)
  putU16(out, f + 5, aimChecksum(payload))
  out[f + 7] = 0x3e
  return out
}

const FRAME_START = ascii.encode('<h')

/** Incremental frame parser; tolerates partial reads and skips corrupt frames. */
export class AimDecoder {
  private buffer: Uint8Array = new Uint8Array(0)

  feed(data: Uint8Array): AimFrame[] {
    this.buffer = this.buffer.length ? concat([this.buffer, data]) : data.slice()
    const out: AimFrame[] = []
    for (;;) {
      const start = indexOf(this.buffer, FRAME_START)
      if (start < 0) {
        this.buffer = this.buffer.subarray(Math.max(0, this.buffer.length - 1))
        return out
      }
      if (start > 0) this.buffer = this.buffer.subarray(start)
      if (this.buffer.length < 12) return out
      const type = utf8.decode(this.buffer.subarray(2, 6)) as FrameType
      const length = u32(this.buffer, 6)
      if ((type !== 'STCP' && type !== 'STNC') || length > AIM_MAX_FRAME_PAYLOAD) {
        this.buffer = this.buffer.subarray(2)
        continue
      }
      const total = 20 + length
      if (this.buffer.length < total) return out
      const payload = this.buffer.slice(12, 12 + length)
      const footer = this.buffer.subarray(12 + length, total)
      this.buffer = this.buffer.subarray(total)
      const footerType = utf8.decode(footer.subarray(0, 5))
      const sum = footer[5] | (footer[6] << 8)
      if (footerType === `<${type}` && footer[7] === 0x3e && sum === aimChecksum(payload)) {
        out.push({ type, payload })
      }
    }
  }
}

// ---------- commands ----------

/**
 * The tag at offset 24 differs between clients: the AiM app (and this logger) use the u32
 * 0x0a01; Race Studio 3's Wireshark frames carry 0x41; delta-bridge sends 0x01.
 */
export type CommandTag = 'tag0a01' | 'byteA' | 'byte1'

export function commandPayload(
  opcode: number,
  subcode: number,
  uploadLength = 0,
  path?: string,
  tag: CommandTag = 'tag0a01',
  extra32?: Uint8Array,
): Uint8Array {
  const p = new Uint8Array(64)
  putU16(p, 8, opcode)
  putU16(p, 10, subcode)
  putU32(p, 16, uploadLength)
  if (tag === 'tag0a01') putU32(p, 24, TAG_REQUEST)
  else p[24] = tag === 'byteA' ? 0x41 : 1
  if (path) {
    const bytes = ascii.encode(path)
    if (bytes.length > 31) throw new AimError('AiM path exceeds 31 bytes', 'INVALID_ARGUMENT')
    p.set(bytes, 32)
  }
  if (extra32) p.set(extra32.subarray(0, 32), 32)
  return p
}

/** The 64-byte clock the session-open uploads: 8 zero bytes, local y/m/d/h/m/s, 8 zero bytes, UTC. */
export function clockUpload(now = new Date()): Uint8Array {
  const d = new Uint8Array(64)
  const put = (at: number, f: number[]) => f.forEach((v, i) => putU32(d, at + i * 4, v))
  put(8, [now.getFullYear(), now.getMonth() + 1, now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()])
  put(40, [now.getUTCFullYear(), now.getUTCMonth() + 1, now.getUTCDate(), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds()])
  return d
}

function ackPayload(offset: number): Uint8Array {
  const p = new Uint8Array(4)
  putU32(p, 0, offset)
  return p
}

/** Device name from the 236-byte discovery reply (NUL-terminated ASCII at 0x14). */
export function parseDiscovery(reply: Uint8Array): AimDevice | null {
  if (reply.length < 0x54) return null
  const raw = reply.subarray(0x14, 0x54)
  const end = raw.indexOf(0)
  const name = utf8.decode(raw.subarray(0, end < 0 ? raw.length : end)).trim()
  return { name: name || 'AiM logger' }
}

// ---------- session list ----------

function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"'
        i++
      } else quoted = !quoted
    } else if (c === ',' && !quoted) {
      out.push(cur.trim())
      cur = ''
    } else cur += c
  }
  if (quoted) throw new AimError('Unterminated quoted session field', 'AIM_BAD_LISTING')
  out.push(cur.trim())
  return out
}

export function parseSessions(payload: Uint8Array): AimSession[] {
  const text = utf8.decode(payload).replaceAll('\0', '')
  const marker = text.indexOf('name,size,date')
  if (marker < 0) throw new AimError('Session list did not contain a CSV header', 'AIM_BAD_LISTING')
  const lines = text.slice(marker).trim().split(/\r?\n/)
  const headers = parseCsvLine(lines[0])
  const sessions: AimSession[] = []
  const names = new Set<string>()
  for (const line of lines.slice(1)) {
    if (!line.trim()) continue
    const values = parseCsvLine(line)
    const row: Record<string, string> = {}
    headers.forEach((h, i) => (row[h] = values[i] ?? ''))
    const name = (row.name ?? '').replaceAll('\\', '/').split('/').at(-1) ?? ''
    const size = Number(row.size)
    // Loggers list other recordings too (e.g. .hrz); only the name must be a plain file name.
    if (!name || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9]{2,4}$/.test(name) || !Number.isSafeInteger(size) || size < 0) {
      throw new AimError(`Session list contains an invalid row: ${line.slice(0, 60)}`, 'AIM_BAD_LISTING')
    }
    if (names.has(name)) throw new AimError(`Duplicate session name: ${name}`, 'AIM_BAD_LISTING')
    names.add(name)
    const laps = Number(row.nlap)
    const best = Number(row.best)
    sessions.push({
      name,
      supported: /\.(xrz|xrk|hrz)$/i.test(name),
      path: `1:/mem/${name}`,
      size,
      date: row.date ?? '',
      time: row.hour || undefined,
      laps: Number.isFinite(laps) && row.nlap ? laps : undefined,
      bestMs: Number.isFinite(best) && row.best ? best : undefined,
      track: row.track_name || undefined,
    })
  }
  return sessions
}

// ---------- client ----------

export type Progress = (received: number, expected: number) => void

interface Echo {
  code: number
  tag: number
  length: number
  stride: number
}

function parseEcho(p: Uint8Array): Echo | null {
  if (p.length !== 64) return null
  return { code: u32(p, 8), tag: u32(p, 24), length: u32(p, 16), stride: u32(p, 20) }
}

const OP = {
  SESSION_OPEN: [0x10, 1],
  INFO: [
    [0x02, 2],
    [0x08, 2],
    [0x03, 2],
  ],
  CATALOG: [0x24, 2],
  READ_FILE: [0x02, 4],
  CLOSE: [0x01, 0],
} as const

export class AimClient {
  private decoder = new AimDecoder()
  private pending: AimFrame[] = []
  private connected = false
  private livenessStarted = false
  private readonly bridge: AimBridge
  private readonly signal?: AbortSignal
  /** Identity from the discovery reply, once known. */
  device: AimDevice | null = null
  /** Short human-readable log of the exchange, for debugging against a real logger. */
  readonly log: string[] = []
  private t0 = Date.now()

  constructor(bridge: AimBridge, signal?: AbortSignal) {
    this.bridge = bridge
    this.signal = signal
  }

  private cancelled() {
    if (this.signal?.aborted) throw new AimError('Operation cancelled', 'CANCELLED')
  }
  private note(s: string) {
    if (this.log.length >= 400) this.log.shift()
    this.log.push(`${((Date.now() - this.t0) / 1000).toFixed(1)}s ${s}`)
  }
  private noteBridge() {
    while (this.bridge.notes?.length) this.note(this.bridge.notes.shift()!)
  }
  private noteFrames(dir: 'rx' | 'tx', frames: AimFrame[]) {
    for (const f of frames) {
      const echo = parseEcho(f.payload)
      const tag = echo ? ` code=0x${echo.code.toString(16)} tag=0x${echo.tag.toString(16)} len=${echo.length} stride=0x${echo.stride.toString(16)}` : ''
      this.note(`${dir} ${f.type} ${f.payload.length}B ${hex(f.payload, echo ? 12 : 20)}${tag}`)
    }
  }

  /** Probe, connect, hello, session-open with the clock, info reads: what the AiM app does. */
  async open(): Promise<void> {
    this.cancelled()
    try {
      const reply = await this.bridge.discover(this.signal)
      this.livenessStarted = true
      this.noteBridge()
      this.device = parseDiscovery(reply)
      if (this.device) this.note(`device: ${this.device.name}`)
      this.cancelled()
      await this.bridge.tcpConnect(this.signal)
      this.connected = true
      this.noteBridge()
      this.decoder = new AimDecoder()
      this.pending = []
      await this.send(HELLO_PAYLOAD)
      const hello = await this.nextFrame(3000)
      if (!(hello.payload.length === 8 && hello.payload[4] === 6 && hello.payload[5] === 9)) {
        throw new AimError(`Unexpected hello from the logger: ${hex(hello.payload)}`, 'AIM_BAD_HELLO')
      }
      // The app pauses ~170 ms between the hello reply and its first command.
      await new Promise((r) => setTimeout(r, 200))
      const extra = new Uint8Array(32)
      putU32(extra, 0, 2)
      const info = await this.transact(OP.SESSION_OPEN, { upload: clockUpload(), extra32: extra })
      this.note(`session open: ${info.length} bytes of device info`)
      for (const op of OP.INFO) {
        const r = await this.transact(op, {})
        this.note(`info 0x${op[0].toString(16)}/${op[1]}: ${r.length} bytes`)
      }
    } catch (e) {
      this.noteBridge()
      await this.close().catch(() => undefined)
      throw staged('aim_startup', e)
    }
  }

  async close(): Promise<void> {
    if (this.connected) {
      // Polite close (tag 0x0a00); the logger sends no reply.
      const bye = commandPayload(OP.CLOSE[0], OP.CLOSE[1], 0, undefined, 'byte1')
      putU32(bye, 24, TAG_CLOSE)
      await this.send(bye).catch(() => undefined)
      this.connected = false
    }
    await this.bridge.tcpClose().catch(() => undefined)
    if (this.livenessStarted) {
      await this.bridge.stopDiscovery().catch(() => undefined)
      this.livenessStarted = false
    }
  }

  async sessions(): Promise<AimSession[]> {
    try {
      // The logger builds the catalog on demand; an empty answer means "not ready yet".
      for (let attempt = 0; attempt < 8; attempt++) {
        const csv = await this.transact(OP.CATALOG, {})
        this.note(`catalog: ${csv.length} bytes`)
        if (csv.length > 0) return parseSessions(csv)
        await new Promise((r) => setTimeout(r, 1500))
      }
      throw new AimError('The logger kept answering with an empty session list', 'AIM_EMPTY')
    } catch (e) {
      throw staged('session_list', e)
    }
  }

  /** Download one session as stored on the logger (zlib-compressed .xrz bytes). */
  async download(session: AimSession, progress?: Progress): Promise<Uint8Array> {
    try {
      const bytes = await this.transact(OP.READ_FILE, { path: session.path, progress })
      if (bytes.length !== session.size) this.note(`size mismatch: list said ${session.size}, got ${bytes.length}`)
      if (bytes.length === 0) throw new AimError(`The logger sent an empty file for ${session.name}`, 'AIM_EMPTY')
      return bytes
    } catch (e) {
      throw staged('download', e)
    }
  }

  // ---- the one transaction every command runs ----

  private async transact(
    op: readonly [number, number],
    o: { upload?: Uint8Array; path?: string; extra32?: Uint8Array; progress?: Progress },
  ): Promise<Uint8Array> {
    const code = ((op[1] << 16) | op[0]) >>> 0
    await this.send(commandPayload(op[0], op[1], o.upload?.length ?? 0, o.path, 'tag0a01', o.extra32))
    if (o.upload) {
      await this.expectEcho(code, TAG_REQUEST)
      const frame = new Uint8Array(4 + o.upload.length)
      frame.set(o.upload, 4)
      await this.send(frame)
      const ack = await this.nextFrame(RESPONSE_TIMEOUT_MS)
      if (ack.payload.length !== 4) this.note(`expected a 4-byte upload ack, got ${ack.payload.length}B`)
    } else {
      await this.expectEcho(code, TAG_ACCEPTED)
    }
    const header = await this.expectEcho(code, TAG_HEADER)
    const expected = header.length
    if (expected === 0) return new Uint8Array(0)
    if (expected > AIM_MAX_RESPONSE) throw new AimError(`Response of ${expected} bytes is too large`, 'AIM_TOO_LARGE')
    const chunks: Uint8Array[] = []
    let received = 0
    let stalls = 0
    await this.send(ackPayload(0))
    while (received < expected) {
      let frame: AimFrame
      try {
        frame = await this.nextFrame(RESPONSE_TIMEOUT_MS)
      } catch (e) {
        // A late chunk is not a dead logger: ask for it again, a few times, before giving up.
        if (!(e instanceof AimError && e.code === 'AIM_TIMEOUT') || ++stalls > CHUNK_RETRIES) throw e
        this.note(`no chunk for ${RESPONSE_TIMEOUT_MS / 1000}s at ${received}/${expected}; asking again (${stalls}/${CHUNK_RETRIES})`)
        await this.send(ackPayload(received))
        continue
      }
      if (frame.payload.length <= 4) continue
      const echo = parseEcho(frame.payload)
      if (echo && echo.tag >= 0x0a00 && echo.tag <= 0x0aff && u32(frame.payload, 0) === 0 && u32(frame.payload, 4) === 0 && echo.code === code) {
        // A repeated control echo; re-ask for where we are.
        await this.send(ackPayload(received))
        continue
      }
      const offset = u32(frame.payload, 0)
      if (offset !== received) {
        this.note(`chunk at ${offset}, expected ${received}; re-asking`)
        await this.send(ackPayload(received))
        continue
      }
      const data = frame.payload.subarray(4)
      if (received + data.length > expected) throw new AimError(`Transfer exceeded the announced ${expected} bytes`, 'AIM_TOO_LARGE')
      chunks.push(data)
      received += data.length
      stalls = 0
      o.progress?.(received, expected)
      if (received < expected) await this.send(ackPayload(received))
    }
    return concat(chunks, expected)
  }

  private async expectEcho(code: number, tag: number): Promise<Echo> {
    const deadline = Date.now() + RESPONSE_TIMEOUT_MS
    while (Date.now() < deadline) {
      let frame: AimFrame
      try {
        frame = await this.nextFrame(deadline - Date.now())
      } catch (e) {
        if (e instanceof AimError && e.code === 'AIM_TIMEOUT') break
        throw e
      }
      const echo = parseEcho(frame.payload)
      if (echo && echo.code === code && echo.tag === tag) return echo
      // A length-0 header for a different-but-related tag: treat any header-like echo for our
      // command with length 0 as "nothing to send".
      if (echo && echo.code === code && tag === TAG_HEADER && echo.tag !== TAG_ACCEPTED && echo.tag !== TAG_REQUEST && echo.length === 0) {
        this.note(`treating tag 0x${echo.tag.toString(16)} with length 0 as an empty response`)
        return echo
      }
      this.note(`skipped ${frame.payload.length}B frame (tag 0x${echo?.tag.toString(16) ?? '-'}) while waiting for echo tag 0x${tag.toString(16)}`)
    }
    throw new AimError(`Logger did not answer command 0x${code.toString(16)} (tag 0x${tag.toString(16)})`, 'AIM_TIMEOUT')
  }

  private send(payload: Uint8Array): Promise<void> {
    this.noteFrames('tx', [{ type: 'STCP', payload }])
    return this.bridge.tcpWrite(encodeAimFrame('STCP', payload), this.signal)
  }

  private async nextFrame(timeoutMs: number): Promise<AimFrame> {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
      this.cancelled()
      const f = this.pending.shift()
      if (f) return f
      const remaining = Math.max(1, deadline - Date.now())
      const frames = this.decoder.feed(await this.bridge.tcpRead(Math.min(1000, remaining), 65_535, this.signal))
      this.noteFrames('rx', frames)
      this.pending.push(...frames)
    }
    throw new AimError('Timed out waiting for a frame from the logger', 'AIM_TIMEOUT')
  }
}

function staged(phase: string, e: unknown): AimError {
  if (e instanceof AimError) {
    if (e.code === 'CANCELLED') return e
    return new AimError(`${phase}: ${e.message}`, e.code)
  }
  return new AimError(`${phase}: ${e instanceof Error ? e.message : String(e)}`, 'AIM_OPERATION_FAILED')
}
